import { useState } from 'react';
import { api, type Question } from '../api';
import { run } from '../hooks';
import type { EditorProps } from '../pages/SitePage';

export function QuestionsEditor({ bundle, reload, show }: EditorProps) {
  const { site, questions, categories } = bundle;
  const add = async () => {
    const r = await run(
      api.createQuestion(site.siteId, {
        text: 'What brings you here today?',
        options: [...categories.slice(0, 4).map((c) => ({ label: c.label, categoryIds: [c.categoryId] })), { label: 'Just browsing', categoryIds: [] }].slice(0, 6),
      }),
      show,
      'Question added',
    );
    if (r) await reload();
  };
  return (
    <div>
      <p className="muted small" style={{ marginBottom: 16 }}>
        The first enabled question is asked when the Springboard opens. Each option maps to one or more categories, which are shown first.
      </p>
      {[...questions].sort((a, b) => a.order - b.order).map((q) => (
        <QuestionBlock key={q.questionId} q={q} categories={categories} siteId={site.siteId} reload={reload} show={show} />
      ))}
      {questions.length === 0 && <div className="empty">No question yet. Visitors go straight to the Springboard.</div>}
      {questions.length < 2 && categories.length >= 2 && (
        <button className="btn" style={{ marginTop: 12 }} onClick={add}>
          + Add question
        </button>
      )}
    </div>
  );
}

function QuestionBlock({ q, categories, siteId, reload, show }: { q: Question; categories: EditorProps['bundle']['categories']; siteId: string; reload: () => Promise<void>; show: (t: string, err?: boolean) => void }) {
  const [text, setText] = useState(q.text);
  const [options, setOptions] = useState(q.options);
  const dirty = text !== q.text || JSON.stringify(options) !== JSON.stringify(q.options);
  const save = async (patch: Partial<Question>) => {
    const r = await run(api.updateQuestion(siteId, q.questionId, patch), show, 'Saved');
    if (r) await reload();
  };
  const setOpt = (i: number, patch: Partial<Question['options'][number]>) => setOptions(options.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  return (
    <section className="category" style={{ marginBottom: 14 }}>
      <div className="category-head">
        <input className="input" style={{ flex: 1, height: 32 }} value={text} onChange={(e) => setText(e.target.value)} aria-label="Question text" />
        <label className="checkbox">
          <input type="checkbox" checked={q.enabled} onChange={(e) => save({ enabled: e.target.checked })} /> enabled
        </label>
        {dirty && (
          <button className="btn sm primary" onClick={() => save({ text, options })}>
            Save
          </button>
        )}
        <button
          className="btn sm danger"
          onClick={async () => {
            if (!confirm('Delete this question?')) return;
            const r = await run(api.deleteQuestion(siteId, q.questionId), show, 'Question deleted');
            if (r !== null) await reload();
          }}
        >
          Delete
        </button>
      </div>
      <div className="items">
        {options.map((o, i) => (
          <div key={o.optionId} className="row" style={{ padding: '4px 0' }}>
            <input className="input" style={{ maxWidth: 260, height: 32 }} value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })} aria-label="Option label" />
            <span className="small muted">shows</span>
            <div className="row">
              {categories.map((c) => (
                <label key={c.categoryId} className="checkbox">
                  <input
                    type="checkbox"
                    checked={o.categoryIds.includes(c.categoryId)}
                    onChange={(e) => setOpt(i, { categoryIds: e.target.checked ? [...o.categoryIds, c.categoryId] : o.categoryIds.filter((x) => x !== c.categoryId) })}
                  />
                  {c.label}
                </label>
              ))}
              {o.categoryIds.length === 0 && <span className="badge">everything</span>}
            </div>
            <button className="btn sm icon danger" title="Remove option" disabled={options.length <= 2} onClick={() => setOptions(options.filter((_, j) => j !== i))}>
              ×
            </button>
          </div>
        ))}
        {options.length < 6 && (
          <button className="btn sm" style={{ justifySelf: 'start' }} onClick={() => setOptions([...options, { optionId: `${q.questionId}o${Date.now().toString(36)}`, label: 'New option', categoryIds: [] }])}>
            + Add option
          </button>
        )}
      </div>
    </section>
  );
}
